// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartScrollerRenderer } from './Renderers/TerceraChartScrollerRenderer';
import { TerceraChartZoomRenderer } from './Renderers/TerceraChartZoomRenderer';
import { CustomEvent } from '@shared/utils/CustomEvents';
export class TerceraChartWindowsContainer {
    constructor(scrollerRenderer, xScaleRenderer, zoomRenderer) {
        // #region Properties
        this.Rectangle = new Rectangle();
        this.Renderers = [];
        this.Windows = [];
        // #endregion
        this.lastTotalH = 0;
        this.lastTotalKoef = 0;
        this.crosshairHeight = 0;
        this.resizeWindows = false;
        this.lastMouseMoveLocation = null;
        this.resizeWinNumber = -1;
        this.AfterResizeWindows = new CustomEvent();
        this.isChartHasMinSize = false;
        this.scrollerRenderer = scrollerRenderer;
        this.Renderers.push(this.scrollerRenderer);
        this.xScaleRenderer = xScaleRenderer;
        this.Renderers.push(this.xScaleRenderer);
        this.zoomRenderer = zoomRenderer;
        this.Renderers.push(this.zoomRenderer);
    }
    Localize() {
        const wcRenderers = this.Renderers;
        let len = wcRenderers.length;
        for (let i = 0; i < len; i++) {
            const wcRenderer = wcRenderers[i];
            if (wcRenderer.Localize) {
                wcRenderer.Localize();
            }
        }
        const windows = this.Windows;
        len = windows.length;
        for (let i = 0; i < len; i++) {
            const allRenderers = windows[i].GetAllRenderers();
            const r_len = allRenderers.length;
            for (let j = 0; j < r_len; j++) {
                const renderer = allRenderers[j];
                if (renderer.Localize) {
                    renderer.Localize();
                }
            }
        }
    }
    // Отрисовка контейнера: внутренние рендереры + рендереры окон
    Draw(gr, advParams) {
        const Windows = this.Windows;
        const beforeRenderers = this.getBeforeRenderers();
        this.drawRenders(gr, beforeRenderers, advParams);
        const windowsLen = Windows.length;
        for (let i = 0; i < windowsLen; i++) {
            const ww = Windows[i];
            ww.Draw(gr, this, advParams);
        }
        const afterRenderers = this.getAfterRenderers();
        this.drawRenders(gr, afterRenderers, advParams);
    }
    // Определить расположение окон
    LayoutWindows() {
        const mainWindow = this.Windows.find((window) => window.IsMainWindow);
        // scroller
        if (!isNullOrUndefined(this.scrollerRenderer) && this.scrollerRenderer.Visible) {
            this.scrollerRenderer.Rectangle.X = mainWindow.ClientRectangle.X;
            this.scrollerRenderer.Rectangle.Y = this.Rectangle.Height - TerceraChartScrollerRenderer.SCROLLER_HEIGHT;
            this.scrollerRenderer.Rectangle.Width =
                this.Rectangle.Width - mainWindow.prevScaleW - mainWindow.ClientRectangle.X;
            this.scrollerRenderer.Rectangle.Height = TerceraChartScrollerRenderer.SCROLLER_HEIGHT;
            this.scrollerRenderer.Visible = true;
        }
        const scrollerH = this.scrollerRenderer.Visible ? this.scrollerRenderer.Rectangle.Height : 0;
        // time scale
        if (!isNullOrUndefined(this.xScaleRenderer) && this.xScaleRenderer.Visible) {
            this.xScaleRenderer.Rectangle.X = mainWindow.ClientRectangle.X;
            this.xScaleRenderer.Rectangle.Y =
                this.Rectangle.Height - scrollerH - this.xScaleRenderer.GetPreferredHeight();
            this.xScaleRenderer.Rectangle.Width =
                this.Rectangle.Width - mainWindow.prevScaleW - mainWindow.ClientRectangle.X;
            this.xScaleRenderer.Rectangle.Height = this.xScaleRenderer.GetPreferredHeight();
        }
        // zoom
        if (!isNullOrUndefined(this.zoomRenderer) && !this.isChartHasMinSize) {
            const SPACE_FROM_BOTTOM = 8;
            const MARGIN = TerceraChartZoomRenderer.MARGIN;
            const CHART_ZOOM_RENDERER_WIDTH = TerceraChartZoomRenderer.SIZE * 3 + TerceraChartZoomRenderer.SPACEBETWEENBTNS * 2;
            const CHART_ZOOM_RENDERER_HEIGHT = TerceraChartZoomRenderer.SIZE;
            this.zoomRenderer.Rectangle.X =
                Math.floor(this.Rectangle.Width / 2 - CHART_ZOOM_RENDERER_WIDTH / 2) - MARGIN;
            this.zoomRenderer.Rectangle.Y =
                this.Rectangle.Height -
                    this.xScaleRenderer.Rectangle.Height -
                    MARGIN -
                    SPACE_FROM_BOTTOM -
                    CHART_ZOOM_RENDERER_HEIGHT;
            this.zoomRenderer.Rectangle.Width = CHART_ZOOM_RENDERER_WIDTH + MARGIN * 2;
            this.zoomRenderer.Rectangle.Height = CHART_ZOOM_RENDERER_HEIGHT + SPACE_FROM_BOTTOM + MARGIN;
        }
        else {
            this.zoomRenderer.Rectangle = new Rectangle();
        }
        const totalH = this.Rectangle.Height - scrollerH - this.xScaleRenderer.Rectangle.Height;
        this.ResizeWindows(totalH);
        this.crosshairHeight = totalH;
    }
    CalculateMinMax() {
        const Windows = this.Windows;
        const len = Windows.length;
        for (let i = 0; i < len; i++) {
            Windows[i].CalculateMinMax();
        }
    }
    ResizeWindows(totalH) {
        const Windows = this.Windows;
        const Rectangle = this.Rectangle;
        // сначала считаем справедливые коэф. независимо от видимости
        let totalHKoef = 0;
        const w_len = Windows.length;
        for (let i = 0; i < w_len; i++) {
            totalHKoef += Windows[i].HeightKoef;
        }
        // затем с учетом видимости пересчитаем коэф.
        let totalHKoefVisible = 0;
        for (let i = 0; i < w_len; i++) {
            const ww = Windows[i];
            // значит на хэдер нужно место, зарезервируем
            if (ww.HeaderVisible && !ww.IsMainWindow) {
                totalH -= ww.HeaderHeight;
            }
            if (!ww.Collapsed) {
                totalHKoefVisible += ww.HeightKoef;
            }
        }
        let curY = 0;
        const AlfaVisible = totalHKoefVisible / totalH;
        for (let i = 0; i < w_len; i++) {
            const ww = Windows[i];
            const wRect = ww.Rectangle;
            wRect.X = 0;
            wRect.Y = curY;
            wRect.Width = Rectangle.Width;
            wRect.Height =
                (!ww.Collapsed ? Math.round(ww.HeightKoef / AlfaVisible) : 0) + // не коллапсед
                    (ww.HeaderVisible && !ww.IsMainWindow ? ww.HeaderHeight : 0); // и добавим место на хэдер
            curY += wRect.Height;
            ww.OnLayout();
        }
        this.lastTotalH = totalH;
        this.lastTotalKoef = totalHKoefVisible;
        if (this.AfterResizeWindows != null) {
            this.AfterResizeWindows.Raise();
        }
    }
    // #region Process mouse events
    ProcessMouseMove(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        let resizeWindows = this.resizeWindows;
        const resizeWinNumber = this.resizeWinNumber;
        const lastTotalH = this.lastTotalH;
        const lastMouseMoveLocation = this.lastMouseMoveLocation;
        const lastTotalKoef = this.lastTotalKoef;
        try {
            let res = false;
            for (let i = 0; i < r_len; i++) {
                res = Renderers[i].ProcessMouseMove(e);
                if (res) {
                    return res;
                }
            }
            for (let i = 0; i < ww_len; i++) {
                const window = Windows[i];
                if (window.collapserRectangle.Contains(e.Location.X, e.Location.Y)) {
                    window.isCollapsedHover = true;
                    e.NeedRedraw = true;
                    return true;
                }
                else if (window.isCollapsedHover) {
                    window.isCollapsedHover = false;
                    e.NeedRedraw = true;
                }
                const windowRenderers = window.GetAllRenderers();
                const wr_len = windowRenderers.length;
                for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                    res = windowRenderers[wr_i].ProcessMouseMove(e);
                    if (res) {
                        return res;
                    }
                }
                // +++ resize windows
                if (resizeWindows && ww_len > 1 && resizeWinNumber > 0) {
                    // delta absolut
                    const yDeltaPy = e.Location.Y - lastMouseMoveLocation.Y;
                    const deltaKoef = (lastTotalKoef * (yDeltaPy / lastTotalH)) / ww_len;
                    // search uncollapsed windows to resize them
                    let lastWin = resizeWinNumber;
                    let firstWin = resizeWinNumber - 1;
                    while (ww_len > lastWin && Windows[lastWin].Collapsed) {
                        lastWin++;
                    }
                    while (Windows[firstWin].Collapsed) {
                        firstWin--;
                    }
                    if (lastWin === ww_len || lastWin === firstWin) {
                        return true;
                    }
                    // resize 1
                    const lastWinWindow = Windows[lastWin];
                    lastWinWindow.HeightKoef -= deltaKoef;
                    if (lastWinWindow.HeightKoef < 10) {
                        // коллапсим окно
                        lastWinWindow.HeightKoef = 10;
                        lastWinWindow.Collapsed = true;
                    }
                    // resize 2
                    const firstWinWindow = Windows[firstWin];
                    firstWinWindow.HeightKoef += deltaKoef;
                    if (firstWinWindow.HeightKoef < 10) {
                        if (!firstWinWindow.IsMainWindow) {
                            // коллапсим окно
                            firstWinWindow.HeightKoef = 10;
                            firstWinWindow.Collapsed = true;
                        }
                        else {
                            // усли ктото удумал сжать мейн окно меньше минимума откатываем последний ресайз и убираем изменение размеров
                            lastWinWindow.HeightKoef += deltaKoef;
                            firstWinWindow.HeightKoef -= deltaKoef;
                            resizeWindows = false;
                            this.resizeWindows = resizeWindows;
                        }
                    }
                    this.LayoutWindows();
                    e.NeedRedraw = true;
                }
            }
        }
        finally {
            this.lastMouseMoveLocation = e.Location;
        }
        return false;
    }
    ProcessMouseDown(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        let res = false;
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMouseDown(e);
            if (res)
                return res;
        }
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseDown(e);
                if (res)
                    return res;
            }
            // для мейн никаких ресайзов, коллапсеров
            if (!ww.IsMainWindow &&
                (ww.windowHeaderRectangle.Contains(e.Location.X, e.Location.Y) ||
                    Math.abs(e.Location.Y - ww.windowHeaderRectangle.Y) <= 4)) {
                this.lastMouseMoveLocation = e.Location;
                this.resizeWindows = true;
                this.resizeWinNumber = i;
                return true;
            }
        }
        return res;
    }
    ProcessMouseUp(e) {
        let res = false;
        this.resizeWindows = false;
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMouseUp(e);
            if (res)
                return res;
        }
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseUp(e);
                if (res)
                    return res;
            }
            if (!ww.IsMainWindow && ww.collapserRectangle.Contains(e.Location.X, e.Location.Y)) {
                ww.Collapsed = !ww.Collapsed;
                this.LayoutWindows();
                ww.UpdateClientRectangle();
            }
        }
        return res;
    }
    OnKeyDown(e) {
        this.resizeWindows = false;
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        for (let i = 0; i < r_len; i++) {
            Renderers[i].OnKeyDown(e);
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const windowRenderers = Windows[i].GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                windowRenderers[wr_i].OnKeyDown(e);
            }
        }
    }
    OnKeyUp(e) {
        this.resizeWindows = false;
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        for (let i = 0; i < r_len; i++) {
            Renderers[i].OnKeyUp(e);
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const windowRenderers = Windows[i].GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                windowRenderers[wr_i].OnKeyUp(e);
            }
        }
    }
    ProcessMouseDoubleClick(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        let res = false;
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMouseDoubleClick(e);
            if (res)
                return res;
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseDoubleClick(e);
                if (res)
                    return res;
            }
            // для мейн никаких ресайзов, коллапсеров
            if (!ww.IsMainWindow) {
                if (ww.collapserRectangle.Contains(e.Location.X, e.Location.Y)) {
                    return true;
                }
                else if (ww.windowHeaderRectangle.Contains(e.Location.X, e.Location.Y) ||
                    Math.abs(e.Location.Y - ww.windowHeaderRectangle.Y) <= 4) {
                    ww.Collapsed = !ww.Collapsed;
                    this.LayoutWindows();
                }
            }
        }
        return res;
    }
    ProcessMouseClick(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        let res = false;
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMouseClick(e);
            if (res) {
                return res;
            }
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseClick(e);
                if (res) {
                    return res;
                }
            }
        }
        return res;
    }
    ProcessMouseWheel(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        let res = false;
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMouseWheel(e);
            if (res) {
                return res;
            }
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseWheel(e);
                if (res) {
                    return res;
                }
            }
        }
        return res;
    }
    ProcessMultiTouches(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        let res = false;
        for (let i = 0; i < r_len; i++) {
            res = Renderers[i].ProcessMultiTouches(e);
            if (res) {
                return res;
            }
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMultiTouches(e);
                if (res) {
                    return res;
                }
            }
        }
        return res;
    }
    ProcessMouseEnter(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        for (let i = 0; i < r_len; i++) {
            Renderers[i].ProcessMouseEnter(e);
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            let res;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseEnter(e);
            }
        }
    }
    ProcessMouseLeave(e) {
        const Renderers = this.Renderers;
        const r_len = Renderers.length;
        const Windows = this.Windows;
        const ww_len = Windows.length;
        // Container renderes
        for (let i = 0; i < r_len; i++) {
            Renderers[i].ProcessMouseLeave(e);
        }
        // Windows renderers
        for (let i = 0; i < ww_len; i++) {
            const ww = Windows[i];
            const windowRenderers = ww.GetAllRenderers();
            const wr_len = windowRenderers.length;
            let res;
            for (let wr_i = 0; wr_i < wr_len; wr_i++) {
                res = windowRenderers[wr_i].ProcessMouseLeave(e);
            }
        }
    }
    // #endregion
    // --------------------------C#
    GetContextMenu(e, chart) {
        for (let i = 0; i < this.Renderers.length; i++) {
            const contextMenu = this.Renderers[i].GetContextMenu(e, chart);
            if (isValidArray(contextMenu)) {
                return contextMenu;
            }
        }
        for (let i = 0; i < this.Windows.length; i++) {
            const curWindow = this.Windows[i];
            for (let j = 0; j < curWindow.Renderers.length; j++) {
                const contextMenu = curWindow.Renderers[j].GetContextMenu(e, chart);
                if (isValidArray(contextMenu)) {
                    return contextMenu;
                }
            }
        }
        return [];
    }
    //    /// <summary>
    //    /// Вернуть курсор для чарта
    //    /// </summary>
    GetCursor(e) {
        let newCursor = null;
        for (let i = 0; i < this.Windows.length; i++) {
            const ww = this.Windows[i];
            const renderers = ww.GetAllRenderers();
            const len_j = renderers.length;
            for (let j = 0; j < len_j; j++) {
                newCursor = renderers[j].GetCursor(e);
                if (newCursor != null) {
                    return newCursor;
                }
            }
            if (!ww.IsMainWindow) // у главного окна нет коллапсера
             {
                if (ww.collapserRectangle.Contains(e.Location.X, e.Location.Y)) {
                    return Cursors.Hand;
                }
                else if (ww.windowHeaderRectangle.Contains(e.Location.X, e.Location.Y) ||
                    Math.abs(e.Location.Y - ww.windowHeaderRectangle.Y) <= 4) {
                    return Cursors.HSplit;
                }
            }
        }
        for (let i = 0; i < this.Renderers.length; i++) {
            newCursor = this.Renderers[i].GetCursor(e);
            if (newCursor != null) {
                return newCursor;
            }
        }
        return newCursor;
    }
    /// <summary>
    /// Вернуть тултип для чарта
    /// </summary>
    GetTooltip(e) {
        let newTooltip = null;
        for (let i = 0; i < this.Renderers.length; i++) {
            newTooltip = this.Renderers[i].GetTooltip(e);
            if (newTooltip != null) {
                return newTooltip;
            }
        }
        for (let i = 0; i < this.Windows.length; i++) {
            const window = this.Windows[i];
            const renderers = window.GetAllRenderers();
            for (let j = 0; j < renderers.length; j++) {
                const renderer = renderers[j];
                newTooltip = renderer.GetTooltip(e);
                if (newTooltip != null) {
                    return newTooltip;
                }
            }
            if (!window.IsMainWindow) // у главного окна нет коллапсера
             {
                if (window.collapserRectangle.Contains(e.Location.X, e.Location.Y)) {
                    return window.Collapsed ? 'Click to expand window' : 'Click to collapse window';
                }
                else if (window.windowHeaderRectangle.Contains(e.Location.X, e.Location.Y) ||
                    Math.abs(e.Location.Y - window.windowHeaderRectangle.Y) <= 4) {
                    return window.Collapsed ? 'Expande or double click to resize window' : 'Drag to resize window';
                }
            }
        }
        return newTooltip;
    }
    Dispose() {
        for (const renderer of this.Renderers) {
            renderer.Dispose();
        }
        this.Renderers = [];
        for (const window of this.Windows) {
            window.Dispose();
        }
        this.Windows = [];
    }
    themeChanged() {
        this.Renderers.forEach((renderer) => {
            renderer.ThemeChanged();
        });
        this.Windows.forEach((window) => {
            window.ThemeChanged();
        });
    }
    getBeforeRenderers() {
        return [this.xScaleRenderer];
    }
    getAfterRenderers() {
        const beforeRenderers = this.getBeforeRenderers();
        const filteredArray = this.Renderers.filter((renderer) => !beforeRenderers.includes(renderer));
        return filteredArray;
    }
    drawRenders(gr, renders, advParams) {
        const mainWindow = advParams.TerceraChart.mainWindow;
        const curLayer = advParams.layerId;
        renders.forEach((curRend) => {
            if (curRend.IsNeedDraw(curLayer)) {
                curRend.Draw(gr, mainWindow, this, advParams);
            }
        });
    }
}
